import dayjs, { type Dayjs } from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { serviceSchedule } from '../services/service';
import type { Employee, Paycode } from '../types/services';
import type { Options } from '../types/hooks';
import { usePermisoSchedule } from '../../../../auth/permisosModulos';

type AmountMode = 'relative_amount' | 'numerical_amount';
type RelativeHours = 1 | 2;

interface UseFormAddPaycodeProps {
    employees: Employee[];
    selectedEmployeeIds: Set<string>;
    reload: () => Promise<void> | void;
    option: Options | null;
    setOption: (value: Options | null) => void;
}

interface JwtPayload {
    user_id: string;
}

interface PaycodeOption {
    value: string;
    label: string;
}

interface FormData {
    paycode: string;
    date: Dayjs | null;
    start: Dayjs | null;
    repeat: string;
    amountDays: string;
    mode: AmountMode;
    amountHours: Dayjs | null;
    amountRelativeHours: RelativeHours;
    note: string;
    comment: string;
}

interface FieldError {
    error: boolean;
    message: string;
}

interface ValidationErrors {
    employeeIds: FieldError;
    paycode: FieldError;
    date: FieldError;
    start: FieldError;
    repeat: FieldError;
    amountDays: FieldError;
    amountHours: FieldError;
}

const initialValidationErrors = (): ValidationErrors => ({
    employeeIds: { error: false, message: '' },
    paycode: { error: false, message: '' },
    date: { error: false, message: '' },
    start: { error: false, message: '' },
    repeat: { error: false, message: '' },
    amountDays: { error: false, message: '' },
    amountHours: { error: false, message: '' },
});

const initialFormData = (): FormData => ({
    paycode: '',
    date: dayjs(),
    start: null,
    repeat: '1',
    amountDays: '1',
    mode: 'relative_amount',
    amountHours: null,
    amountRelativeHours: 1,
    note: '',
    comment: '',
});

const useFormAddPaycode = ({ employees, selectedEmployeeIds, reload, option, setOption }: UseFormAddPaycodeProps) => {
    const { t } = useTranslation('schedule.form.add_paycode');
    const { showAlert } = useRbSystemAlert();
    const canAddPaycode = usePermisoSchedule('add_paycode');

    const [openForm, setOpenForm] = useState(false);
    const [isJustifyingShift, setIsJustifyingShift] = useState(false);
    const [employeeIds, setEmployeeIds] = useState<string[]>([]);
    const [searchEmployee, setSearchEmployee] = useState('');
    const [paycodes, setPaycodes] = useState<PaycodeOption[]>([]);
    const [searchPaycode, setSearchPaycode] = useState('');
    const [formData, setFormData] = useState<FormData>(initialFormData());
    const [loading, setLoading] = useState(false);
    const [loadingPaycodes, setLoadingPaycodes] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    useEffect(() => {
        if (option === 'add_paycode_assign' || option === 'justify_shift') {
            setEmployeeIds(Array.from(selectedEmployeeIds));
            if (canAddPaycode) setOpenForm(true);
            setOption(null);
        }
    }, [option, selectedEmployeeIds, setOption, canAddPaycode]);

    const resetForm = () => {
        setIsJustifyingShift(false);
        setEmployeeIds([]);
        setSearchEmployee('');
        setSearchPaycode('');
        setFormData(initialFormData());
        setValidationMessage('');
        setShowMessage(false);
        setSubmitAttempted(false);
    };

    const closeForm = () => {
        setOpenForm(false);
        resetForm();
    };

    const hasChanges = () => {
        if (employeeIds.length > 0) return true;
        const initial = initialFormData();
        return (
            formData.paycode !== initial.paycode ||
            (formData.date?.valueOf() ?? null) !== (initial.date?.valueOf() ?? null) ||
            formData.start !== initial.start ||
            formData.repeat !== initial.repeat ||
            formData.amountDays !== initial.amountDays ||
            formData.mode !== initial.mode ||
            formData.amountHours !== initial.amountHours ||
            formData.amountRelativeHours !== initial.amountRelativeHours ||
            formData.note !== initial.note ||
            formData.comment !== initial.comment
        );
    };

    const handleCancel = () => {
        if (hasChanges()) {
            setShowMessage(true);
        } else {
            closeForm();
        }
    };

    const fetchPaycodes = async () => {
        const { paycodes: list } = await serviceSchedule.getPaycodes({
            params: { t: 'standard', u: 'days' },
            alert: { t, showAlert },
        });

        setPaycodes(
            list
                .filter((paycode: Paycode) => paycode.unit === 'days')
                .map((paycode: Paycode) => ({
                    value: paycode.paycode_id,
                    label: paycode.name,
                })),
        );
    };

    useEffect(() => {
        if (option === 'add_paycode_assign' || option === 'justify_shift') {
            setIsJustifyingShift(option === 'justify_shift');
            if (canAddPaycode) setOpenForm(true);
            setOption(null);
        }
    }, [option, setOption, canAddPaycode]);

    useEffect(() => {
        if (!openForm) return;

        setLoadingPaycodes(true);
        fetchPaycodes().finally(() => setLoadingPaycodes(false));
    }, [openForm]);

    const buildErrors = (): ValidationErrors => {
        const nextErrors = initialValidationErrors();

        if (employeeIds.length === 0) {
            nextErrors.employeeIds = { error: true, message: t('add_paycode.helper.worker') };
        }
        if (!formData.paycode) {
            nextErrors.paycode = { error: true, message: t('add_paycode.helper.paycode') };
        }
        if (!formData.date?.isValid()) {
            nextErrors.date = { error: true, message: t('add_paycode.helper.date') };
        }
        if (!formData.start?.isValid()) {
            nextErrors.start = { error: true, message: t('add_paycode.helper.start_at') };
        }
        const repeat = Number(formData.repeat);
        if (!Number.isInteger(repeat) || repeat < 1) {
            nextErrors.repeat = { error: true, message: t('add_paycode.helper.repeat') };
        }
        const amountDays = Number(formData.amountDays);
        if (amountDays !== 0.5 && amountDays !== 1) {
            nextErrors.amountDays = { error: true, message: t('add_paycode.helper.amount_of_days') };
        }
        if (formData.mode === 'numerical_amount' && !formData.amountHours?.isValid()) {
            nextErrors.amountHours = { error: true, message: t('add_paycode.helper.amount_of_hours') };
        }

        return nextErrors;
    };

    // Derived: errors are shown only after the first submit attempt and auto-clear as fields become valid.
    const validationErrors = useMemo<ValidationErrors>(() => (submitAttempted ? buildErrors() : initialValidationErrors()), [submitAttempted, formData, employeeIds, t]);

    const submit = async () => {
        if (!canAddPaycode) return;

        const accessToken = localStorage.getItem('access_token');

        if (!accessToken) {
            setValidationMessage(t('add_paycode.alerts.validation.no_session'));
            return;
        }

        setSubmitAttempted(true);
        if (Object.values(buildErrors()).some((e) => e.error)) return;

        let userId: number;
        try {
            const decoded = jwtDecode<JwtPayload>(accessToken);
            userId = parseInt(decoded.user_id, 10);
            if (Number.isNaN(userId)) throw new Error('invalid user_id');
        } catch {
            setValidationMessage(t('add_paycode.alerts.validation.no_session'));
            return;
        }

        setLoading(true);

        const startDate = formData.date;
        const startTime = formData.start;
        const promises: Promise<boolean>[] = [];
        const repeat = Number(formData.repeat);
        const amountDays = Number(formData.amountDays);

        for (let offset = 0; offset < repeat; offset++) {
            const day = startDate?.add(offset, 'day');

            for (const workerId of employeeIds) {
                promises.push(
                    serviceSchedule.assignPaycode({
                        body: {
                            amount: formData.amountHours?.format('HH:mm') ?? null,
                            amount_day: amountDays,
                            comment: formData.comment,
                            create_by: userId,
                            date: day ? day.format('YYYY-MM-DD') : '',
                            id_employee: parseInt(workerId, 10),
                            id_paycode: parseInt(formData.paycode, 10),
                            lastupdate_by: userId,
                            note: formData.note,
                            start: startTime ? startTime.format('HH:mm') : '',
                            template: 'Scheduler',
                            type: formData.mode === 'relative_amount' ? formData.amountRelativeHours : null,
                        },
                        alert: { t, showAlert },
                    }),
                );
            }
        }

        try {
            const results = await Promise.all(promises);
            const allOk = results.every(Boolean);

            if (allOk) {
                showAlert('success', t('add_paycode.alerts.save_success.message'));
                await reload();
                closeForm();
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        t,
        title: t(isJustifyingShift ? 'add_paycode.justify_shift_title' : 'add_paycode.title'),
        openForm,
        setOpenForm,
        closeForm,
        employees,
        employeeIds,
        setEmployeeIds,
        searchEmployee,
        setSearchEmployee,
        paycodes,
        searchPaycode,
        setSearchPaycode,
        loadingPaycodes,
        formData,
        setFormData,
        loading,
        validationMessage,
        setValidationMessage,
        showMessage,
        setShowMessage,
        validationErrors,
        hasChanges,
        handleCancel,
        submit,
    };
};

export { useFormAddPaycode };
